// Persistent record of an in-flight multipart upload, so a big upload that
// was interrupted (tab closed, crash) can be RESUMED instead of restarted.
//
// The browser constraint: a File's bytes cannot be re-read after a reload
// without the user re-selecting the file (security). So resume is: persist
// which chunks are done, and when the user re-adds a matching file (same
// name + size + lastModified), skip the done chunks and finish the rest.
// Chromium can skip the re-selecting: a FileSystemFileHandle survives a
// reload in IndexedDB, and one click re-grants read access.
//
// The record also carries where the file was going (folder, overwrite,
// editor extras), written right after upload-mpu-create — a resume used to
// complete into whatever folder the user happened to be looking at.
//
// Raw IndexedDB (no dependency) — one object store keyed by r2Key.

export interface ResumeRecord {
  r2Key: string;
  uploadId: string;
  fileName: string;
  fileSize: number;
  fileLastModified: number;
  contentType: string;
  chunkSize: number;
  parts: Array<{ partNumber: number; etag: string }>; // completed parts
  updatedAt: number;
  /** Where upload-complete files it, so a resume lands where the upload was going. */
  folder?: string;
  overwriteAssetId?: string;
  deferIndexing?: boolean;
  completeExtra?: { editedFromAssetId?: string; width?: number; height?: number };
  /** Account that started it — two accounts on one browser share this store. */
  owner?: string;
  /** Chromium only: lets a resume re-read the file after a reload without a picker. */
  handle?: FileSystemFileHandle;
  /** Never reached upload-mpu-create (still waiting in the queue): uploadId is ''. */
  queued?: boolean;
  createdAt?: number;
}

/** Fields a caller can attach to a record; putMultipart keeps them on every write. */
export type ResumeMeta = Pick<ResumeRecord, 'folder' | 'overwriteAssetId' | 'deferIndexing' | 'completeExtra' | 'owner' | 'handle' | 'createdAt'>;

const DB = 'soltik-uploads';
const STORE = 'multipart';

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'r2Key' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T | null> {
  const db = await open();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const t = db.transaction(STORE, mode);
      const req = fn(t.objectStore(STORE));
      req.onsuccess = () => resolve(req.result as T);
      req.onerror = () => resolve(null);
      t.oncomplete = () => db.close();
    } catch { resolve(null); }
  });
}

export const resumeStore = {
  async put(rec: ResumeRecord): Promise<void> {
    const row = { ...rec, updatedAt: Date.now() };
    // A browser that cannot clone the handle must still keep the record.
    const ok = await tx('readwrite', (s) => s.put(row)).catch(() => null);
    if (ok === null && row.handle) {
      const { handle: _drop, ...rest } = row;
      await tx('readwrite', (s) => s.put(rest));
    }
  },
  async get(r2Key: string): Promise<ResumeRecord | null> {
    return tx<ResumeRecord>('readonly', (s) => s.get(r2Key));
  },
  async all(): Promise<ResumeRecord[]> {
    const r = await tx<ResumeRecord[]>('readonly', (s) => s.getAll());
    return r || [];
  },
  async delete(r2Key: string): Promise<void> {
    await tx('readwrite', (s) => s.delete(r2Key));
  },
  /** Match a freshly-selected File to a saved record (same identity). */
  matches(rec: Pick<ResumeRecord, 'fileName' | 'fileSize' | 'fileLastModified'>, f: Pick<File, 'name' | 'size' | 'lastModified'>): boolean {
    return rec.fileName === f.name && rec.fileSize === f.size && rec.fileLastModified === f.lastModified;
  },
};

/**
 * The saved multipart this file can continue, if any. Skips queued entries
 * (nothing on the server to continue), other accounts' records, and keys
 * that are uploading right now — resuming a live upload from a second place
 * sends the same parts twice and, once one side completes, restarts the
 * other from zero.
 */
export function findResumeRecord(
  records: ResumeRecord[],
  file: Pick<File, 'name' | 'size' | 'lastModified'>,
  opts: { owner?: string | null; busy?: (r2Key: string) => boolean } = {},
): ResumeRecord | null {
  return records.find((r) => !r.queued && !!r.uploadId
    && ownedBy(r, opts.owner)
    && !opts.busy?.(r.r2Key)
    && resumeStore.matches(r, file)) || null;
}

/**
 * Is this record the signed-in account's? Two accounts on one browser share
 * the store. Records older than the owner field are claimed by their key
 * (media/<userId>/…); with no known account, nothing is.
 */
export function ownedBy(rec: ResumeRecord, owner?: string | null): boolean {
  if (!owner) return false;
  if (rec.owner) return rec.owner === owner;
  return keyOwner(rec.r2Key) === owner;
}

/** The user id an upload key belongs to — the server mints media/<userId>/… */
export function keyOwner(r2Key: string): string | null {
  const m = /^media\/([^/]+)\//.exec(r2Key);
  return m ? m[1] : null;
}

/** Server-side window: the hourly cron aborts multipart uploads open longer than this. */
export const RESUME_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Split saved records into what to offer for resume and what to forget.
 * `openKeys` is the server's still-open list, or null when it could not be
 * read — an unreadable list is not proof the uploads are gone, so nothing is
 * deleted then. Keys busy in some tab are neither offered nor deleted.
 */
export function reconcileResumeRecords(
  records: ResumeRecord[],
  openKeys: Set<string> | null,
  opts: { owner?: string | null; busy?: (r2Key: string) => boolean; now?: number } = {},
): { live: ResumeRecord[]; stale: ResumeRecord[] } {
  const now = opts.now ?? Date.now();
  const live: ResumeRecord[] = [];
  const stale: ResumeRecord[] = [];
  for (const r of records) {
    if (!ownedBy(r, opts.owner) || opts.busy?.(r.r2Key)) continue;
    if (r.queued) {
      if (now - (r.createdAt ?? r.updatedAt) > RESUME_WINDOW_MS) stale.push(r);
      else live.push(r);
      continue;
    }
    if (!openKeys) continue;
    if (openKeys.has(r.r2Key)) live.push(r);
    else stale.push(r);
  }
  return { live, stale };
}
